import { DefaultChatTransport, type ChatTransport, type HttpChatTransportInitOptions, type UIMessage } from "ai";
import { operationFor, type CopilotOperation } from "./copilot-session-fetch";

type Options = Omit<HttpChatTransportInitOptions<UIMessage>, "headers" | "fetch"> & {
  onAssistantStart?: (messageId: string, operation: CopilotOperation) => void;
};

/** Each operation owns a capsule and SDK instance; overlapping turns share no credentials. */
export function createCopilotChatTransport({ onAssistantStart, ...config }: Options): ChatTransport<UIMessage> {
  const assistantIds = new Set<string>();
  function bindAssistant(stream: Awaited<ReturnType<ChatTransport<UIMessage>["sendMessages"]>>, operation: CopilotOperation) {
    let assistantId: string | undefined;
    return operation.guardStream(stream.pipeThrough(new TransformStream({
      transform(chunk, controller) {
        operation.assertCurrent();
        if (chunk.type === "start") {
          if (!assistantId) {
            assistantId = chunk.messageId && !assistantIds.has(chunk.messageId) ? chunk.messageId : crypto.randomUUID();
            assistantIds.add(assistantId);
            onAssistantStart?.(assistantId, operation);
          }
          controller.enqueue({ ...chunk, messageId: assistantId });
        } else controller.enqueue(chunk);
      },
    })));
  }
  return {
    async sendMessages(request) {
      const operation = operationFor(request.metadata);
      operation.assertCurrent();
      const payload = { ...request, messages: structuredClone(request.messages), body: structuredClone(request.body) };
      for (const message of payload.messages) if (message.role === "assistant") assistantIds.add(message.id);
      const sdk = new DefaultChatTransport({ ...config, headers: operation.headers, fetch: operation.fetch });
      const stream = await sdk.sendMessages(payload);
      // The SDK parses/buffers SSE asynchronously. Guard parsed frames as well.
      return bindAssistant(stream, operation);
    },
    async reconnectToStream(request) {
      const operation = operationFor(request.metadata);
      operation.assertCurrent();
      const sdk = new DefaultChatTransport({ ...config, headers: operation.headers, fetch: operation.fetch });
      const stream = await sdk.reconnectToStream({ ...request, body: structuredClone(request.body) });
      return stream ? bindAssistant(stream, operation) : null;
    },
  };
}
